import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../components/ui/carousel';
import ScrollAnimation from '../components/ScrollAnimation';
import { services, testimonials, companyInfo } from '../data/mock';
import { 
  Bot, 
  MessageSquare, 
  Eye, 
  FileText, 
  Settings,
  ArrowRight,
  Star,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useState } from 'react';

const iconMap = {
  Bot,
  MessageSquare,
  Eye,
  FileText,
  Settings
};

const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <ScrollAnimation>
            <h1 className="text-6xl md:text-6xl font-league-spartan font-light text-gray-900 mb-6 tracking-tight leading-none">
            AI accelerated experiences for digital products of the future
            </h1> 
          </ScrollAnimation>
          <ScrollAnimation delay={200}>
            <p className="text-xl md:text-2xl text-gray-600 font-league-spartan font-light mb-12 max-w-3xl mx-auto leading-relaxed">
              {companyInfo.tagline}
            </p>
          </ScrollAnimation>
          <ScrollAnimation delay={400}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                asChild
                size="lg"
              >
                <Link to="/services" className="flex items-center gap-2">
                  Explore Our Services
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
              >
                <Link to="/contact">Get a Quote</Link>
              </Button>
            </div>
          </ScrollAnimation>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ScrollAnimation className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-league-spartan font-light text-gray-900 mb-6 tracking-tight">
              Our AI Solutions
            </h2>
            <p className="text-lg text-gray-600 font-league-spartan max-w-2xl mx-auto leading-relaxed">
              Cutting-edge artificial intelligence services designed to transform your business and drive innovation.
            </p>
          </ScrollAnimation>

          <Carousel className="relative">
            <CarouselContent>
              {services.map((service, index) => {
                const Icon = iconMap[service.icon];
                return (
                  <CarouselItem key={service.id} className="basis-full md:basis-1/2 lg:basis-1/3">
                    <ScrollAnimation delay={index * 100} className="h-full">
                      <Card className="h-full bg-white hover:bg-gray-50 border border-gray-200 hover:border-primary/20 transition-all duration-300 hover:shadow-lg group">
                        <CardContent className="p-6 h-full flex flex-col">
                          <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary transition-colors duration-300">
                            <Icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" />
                          </div>
                          <h3 className="text-xl font-league-spartan font-medium text-gray-900 mb-3 leading-tight">
                            {service.title}
                          </h3>
                          <p className="text-gray-600 font-league-spartan leading-relaxed flex-grow">
                            {service.description}
                          </p>
                          <div className="mt-5">
                            <Link 
                              to="/services"
                              className="text-primary hover:text-primary/80 font-medium text-sm inline-flex items-center gap-1 transition-colors group-hover:gap-2 duration-200"
                            >
                              Learn More
                              <ArrowRight className="h-4 w-4" />
                            </Link>
                          </div>
                        </CardContent>
                      </Card>
                    </ScrollAnimation>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex" />
            <CarouselNext className="hidden md:flex" />
          </Carousel>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <ScrollAnimation className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-league-spartan font-light text-gray-900 mb-6 tracking-tight">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600 font-league-spartan leading-relaxed">
              Trusted by innovative companies worldwide
            </p>
          </ScrollAnimation>

          <ScrollAnimation delay={200}>
            <div className="relative">
              <Card className="bg-white border-0 shadow-lg">
                <CardContent className="p-8 md:p-12 text-center">
                  <div className="flex justify-center mb-6">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-primary fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-xl md:text-2xl text-gray-800 font-league-spartan font-light leading-relaxed mb-8 italic">
                    "{testimonials[currentTestimonial].quote}"
                  </blockquote>
                  <div>
                    <div className="font-league-spartan font-medium text-gray-900 text-lg">
                      {testimonials[currentTestimonial].name}
                    </div>
                    <div className="text-gray-600 font-league-spartan">
                      {testimonials[currentTestimonial].title}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Navigation buttons */}
              <button
                onClick={prevTestimonial}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-primary hover:shadow-xl transition-all duration-200"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={nextTestimonial}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-primary hover:shadow-xl transition-all duration-200"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>

            {/* Indicators */}
            <div className="flex justify-center mt-8 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentTestimonial 
                      ? 'bg-primary' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </ScrollAnimation>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <ScrollAnimation>
            <h2 className="text-4xl md:text-5xl font-league-spartan font-light text-gray-900 mb-6 tracking-tight">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-gray-600 font-league-spartan mb-12 leading-relaxed">
              Let's discuss how our AI solutions can drive innovation and growth for your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
              >
                <Link to="/contact" className="flex items-center gap-2">
                  Get Started Today
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
              >
                <Link to="/services">View All Services</Link>
              </Button>
            </div>
          </ScrollAnimation>
        </div>
      </section>
    </div>
  );
};

export default Home;